import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { platformSettings } from '@/api/platformSettings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Volume2, Repeat2, Languages } from 'lucide-react';
import { cn } from '@/lib/cn';

export function TtsCacheStats() {
  const me = useCurrentUser();
  if (!me.is_super_admin) return <Navigate to="/dashboard" replace />;
  return <TtsCacheStatsInner />;
}

function TtsCacheStatsInner() {
  const { data, isLoading } = useQuery({
    queryKey: ['tts-cache-stats'],
    queryFn: () => platformSettings.getTtsCacheStats(),
    refetchInterval: 60_000,
  });

  const entries = data?.entries ?? 0;
  const totalHits = data?.total_use_count ?? 0;
  const savedSynths = Math.max(0, totalHits - entries); // hits beyond the first one each = avoided synthesis

  return (
    <>
      <PageHeader
        eyebrow="Platform settings"
        title="TTS cache"
        description="Amazon Polly output is cached by (text · language · voice · engine). Every cache hit is a synthesis bill we didn't pay."
      />

      <div className="max-w-5xl space-y-6">
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={Volume2}
            label="Cached phrases"
            value={entries}
            sublabel="unique (text · language · voice)"
            loading={isLoading}
          />
          <StatCard
            icon={Repeat2}
            label="Total playbacks"
            value={totalHits}
            sublabel="every lookup on an indexed row"
            loading={isLoading}
            accent="emerald"
          />
          <StatCard
            icon={Languages}
            label="Synthesis saved"
            value={savedSynths}
            sublabel="Polly calls we skipped"
            loading={isLoading}
            accent="violet"
          />
        </section>

        <section>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-2">
            Most replayed
          </div>
          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-6 text-sm text-muted-foreground">Loading…</div>
              ) : !data || data.most_used.length === 0 ? (
                <div className="p-10 text-center">
                  <Volume2 className="mx-auto h-6 w-6 text-slate-300 mb-2" />
                  <p className="text-xs text-muted-foreground">
                    No cached phrases yet. The first synthesis of any prompt populates the cache.
                  </p>
                </div>
              ) : (
                <ol className="divide-y divide-slate-100">
                  {data.most_used.map((row, idx) => (
                    <li
                      key={`${idx}-${row.text.slice(0, 16)}`}
                      className="grid grid-cols-[2.5rem_minmax(0,1fr)_4rem_5rem] items-center gap-3 px-4 py-3"
                    >
                      <span className="text-xs font-mono tabular-nums text-slate-400">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <p className="text-sm truncate" title={row.text}>
                        {row.text}
                      </p>
                      <LangChip lang={row.language} />
                      <span className="text-sm font-mono tabular-nums text-right">
                        {row.use_count.toLocaleString()}
                        <span className="text-[10px] text-muted-foreground ml-1">hits</span>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </>
  );
}

type Accent = 'slate' | 'emerald' | 'violet';

function StatCard({
  icon: Icon, label, value, sublabel, loading, accent = 'slate',
}: {
  icon: typeof Volume2;
  label: string;
  value: number;
  sublabel: string;
  loading: boolean;
  accent?: Accent;
}) {
  const accents: Record<Accent, string> = {
    slate: 'text-slate-700',
    emerald: 'text-emerald-600',
    violet: 'text-violet-600',
  };
  return (
    <div className="relative rounded-lg border-2 border-slate-900 bg-white p-5 overflow-hidden">
      <div className="absolute top-0 right-0 p-5 opacity-[0.04] pointer-events-none">
        <Icon className="h-24 w-24" strokeWidth={1} />
      </div>
      <div className="relative">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-2">
          {label}
        </div>
        <div className={cn('text-4xl font-bold tabular-nums font-mono leading-none mb-2', accents[accent])}>
          {loading ? (
            <span className="inline-block h-10 w-20 bg-slate-100 animate-pulse rounded" />
          ) : (
            value.toLocaleString()
          )}
        </div>
        <div className="text-xs text-muted-foreground">{sublabel}</div>
      </div>
    </div>
  );
}

function LangChip({ lang }: { lang: string }) {
  const l = lang.toLowerCase();
  const cls =
    l === 'en' ? 'bg-sky-50 text-sky-900 border-sky-200' :
    l === 'he' ? 'bg-violet-50 text-violet-900 border-violet-200' :
    l === 'yi' ? 'bg-amber-50 text-amber-900 border-amber-200' :
    'bg-slate-50 text-slate-900 border-slate-200';
  return (
    <span
      className={cn(
        'inline-block text-[10px] font-mono uppercase tracking-widest border rounded px-2 py-0.5',
        cls,
      )}
    >
      {l}
    </span>
  );
}
